import { courseCatalogSlots } from '@/features/catalog/course-catalog/paths'
import { courseAdvancedFieldsSlot, courseFormSectionsSlot } from '@/features/catalog/courses/paths'
import { classDetailsSlot } from '@/features/classes/class-form/class'
import { classSlots } from '@/features/classes/slots'
import { organizationDetailsSlot } from '@/features/site-management/organizations/organization'
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
// The slot names' own module, not the users folder's index, which would pull its chooser into the main bundle.
import { userDetailsSlot } from '@/features/site-management/users/user'
import { detailsSlots } from '@/shared/details'
import { column, type Feature } from '@/shared/registry'
import { myDataSlot } from '@/shared/search/my-data'
import { CalendarClock, ClipboardList, Clock, FilePlus2, KeyRound, PlayCircle, Plus, Ticket, UserPlus } from '@/shared/ui/icons'

import { bulkEnrollmentPath, classEnrollmentsPath, classEnrollmentsRoute, createClassEnrollmentPath } from './class-enrollments/class-enrollments'
import type { RosterCellValue } from './class-enrollments/roster-draft'
import { classTrainingKeysPath, classTrainingKeysRoute } from './class-keys/paths'
import { createCustomFieldPath, customFieldRoute, customFieldsPath, editCustomFieldRoute, readCustomFieldsPermission, writeCustomFieldsPermission } from './custom-fields/paths'
import {
  courseAssignmentRoute,
  courseAssignmentsPath,
  createCourseAssignmentPath,
  createPersonCourseAssignmentPath,
  editCourseAssignmentRoute,
  personCourseAssignmentsPath,
} from './course-assignments/paths'
import {
  createEnrollmentPath,
  createPersonEnrollmentPath,
  editEnrollmentRoute,
  enrollmentRoute,
  readEnrollmentsPermission,
  retakeEnrollmentRoute,
  writeEnrollmentsPermission,
} from './enrollments/paths'
import { findEnrollmentsPath, personEnrollmentsPath } from './find-enrollments/paths'
import { myDataApi } from './my-data/paths'
import { myTrainingPath, personTrainingPath, personTrainingRoute, studentDashboardPath, studentDashboardSlots } from './my-training/paths'

import { createPlanTagPath, editPlanTagRoute, planTagRoute, planTagsPath, readPlanTagsPermission, writePlanTagsPermission } from './plan-tags/paths'
import { createPlanPath, editPlanRoute, planRoute, plansPath, readPlansPermission, writePlansPermission } from './plans/paths'
import { PlanReachProvider } from './plans/PlanReachProvider'
import { redeemPath } from './redeem/paths'
import { subscriptionCatalogPath } from './subscription-catalog/paths'
import {
  createPathSubscriptionPath,
  createPersonSubscriptionPath,
  createSubscriptionPath,
  editSubscriptionRoute,
  personSubscriptionsPath,
  readSubscriptionsPermission,
  subscriptionRoute,
  subscriptionsPath,
  writeSubscriptionsPermission,
} from './subscriptions/paths'
import { grantSurveyPath, readSurveyGrantsPermission, surveyGrantsPath, writeSurveyGrantsPermission } from './survey-grants/paths'
import {
  createTrainingKeysPath,
  editTrainingKeyRoute,
  readTrainingKeysPermission,
  trainingKeyRoute,
  trainingKeysPath,
  writeTrainingKeysPermission,
} from './training-keys/paths'
import {
  classVouchersPath,
  classVouchersRoute,
  createVouchersPath,
  editVoucherRoute,
  manageVouchersPath,
  organizationVouchersPath,
  organizationVouchersRoute,
  readVouchersPermission,
  voucherRoute,
  vouchersPath,
  writeVouchersPermission,
} from './vouchers/paths'

/** The scopes that open a class's or an organization's voucher pages: through its organization. */
const voucherPageScopes = ['managedOrganizations', 'allOrganizations']

/** Entitlements: enrollments, subscriptions, training keys and vouchers. Its screens register here. */
export const feature: Feature = {
  id: 'entitlements',
  // Links a plan's name only for a viewer who may open it.
  provider: PlanReachProvider,
  // Find, Create, Details and Edit Training Key: course and plan keys (ENT-14).
  routes: [
    {
      path: trainingKeysPath,
      titleId: 'entitlements.trainingKeys.find.title',
      page: () => import('./training-keys/FindTrainingKeysPage').then((module) => module.FindTrainingKeysPage),
    },
    {
      path: createTrainingKeysPath,
      titleId: 'entitlements.trainingKeys.create.title',
      page: () => import('./training-keys/TrainingKeyFormPages').then((module) => module.CreateTrainingKeysPage),
    },
    {
      path: trainingKeyRoute,
      titleId: 'entitlements.trainingKeys.details.title',
      page: () => import('./training-keys/TrainingKeyDetailsPage').then((module) => module.TrainingKeyDetailsPage),
    },
    {
      path: editTrainingKeyRoute,
      titleId: 'entitlements.trainingKeys.edit.title',
      page: () => import('./training-keys/TrainingKeyFormPages').then((module) => module.EditTrainingKeyPage),
    },
    // Training Keys from Class Details: the class's single-use keys (ENT-15).
    {
      path: classTrainingKeysRoute,
      titleId: 'entitlements.classKeys.title',
      page: () => import('./class-keys/ClassTrainingKeysPage').then((module) => module.ClassTrainingKeysPage),
    },
    // Find, Create, Details and Edit Subscription Profile: subscription plans (ENT-08).
    { path: plansPath, titleId: 'entitlements.plans.find.title', page: () => import('./plans/FindPlansPage').then((module) => module.FindPlansPage) },
    { path: createPlanPath, titleId: 'entitlements.plans.create.title', page: () => import('./plans/PlanFormPages').then((module) => module.CreatePlanPage) },
    { path: planRoute, titleId: 'entitlements.plans.details.title', page: () => import('./plans/PlanDetailsPage').then((module) => module.PlanDetailsPage) },
    { path: editPlanRoute, titleId: 'entitlements.plans.edit.title', page: () => import('./plans/PlanFormPages').then((module) => module.EditPlanPage) },
    // Find, Create, Details and Edit Subscription Profile Tag (ENT-09).
    {
      path: planTagsPath,
      titleId: 'entitlements.planTags.find.title',
      page: () => import('./plan-tags/FindPlanTagsPage').then((module) => module.FindPlanTagsPage),
    },
    {
      path: createPlanTagPath,
      titleId: 'entitlements.planTags.create.title',
      page: () => import('./plan-tags/PlanTagFormPages').then((module) => module.CreatePlanTagPage),
    },
    {
      path: planTagRoute,
      titleId: 'entitlements.planTags.details.title',
      page: () => import('./plan-tags/PlanTagDetailsPage').then((module) => module.PlanTagDetailsPage),
    },
    {
      path: editPlanTagRoute,
      titleId: 'entitlements.planTags.edit.title',
      page: () => import('./plan-tags/PlanTagFormPages').then((module) => module.EditPlanTagPage),
    },
    // Find, Create, Details and Edit Subscription (ENT-10).
    {
      path: subscriptionsPath,
      titleId: 'entitlements.subscriptions.find.title',
      page: () => import('./subscriptions/FindSubscriptionsPage').then((module) => module.FindSubscriptionsPage),
    },
    {
      path: createSubscriptionPath,
      titleId: 'entitlements.subscriptions.create.title',
      page: () => import('./subscriptions/SubscriptionFormPages').then((module) => module.CreateSubscriptionPage),
    },
    // Create Learning Path Subscription (ENT-12).
    {
      path: createPathSubscriptionPath,
      titleId: 'entitlements.subscriptions.createPath.title',
      page: () => import('./subscriptions/SubscriptionFormPages').then((module) => module.CreatePathSubscriptionPage),
    },
    {
      path: subscriptionRoute,
      titleId: 'entitlements.subscriptions.details.title',
      page: () => import('./subscriptions/SubscriptionDetailsPage').then((module) => module.SubscriptionDetailsPage),
    },
    {
      path: editSubscriptionRoute,
      titleId: 'entitlements.subscriptions.edit.title',
      page: () => import('./subscriptions/SubscriptionFormPages').then((module) => module.EditSubscriptionPage),
    },
    // Find, Create, Details and Edit Course Assignment (ENT-11).
    {
      path: courseAssignmentsPath,
      titleId: 'entitlements.courseAssignments.find.title',
      page: () => import('./course-assignments/FindCourseAssignmentsPage').then((module) => module.FindCourseAssignmentsPage),
    },
    {
      path: createCourseAssignmentPath,
      titleId: 'entitlements.courseAssignments.create.title',
      page: () => import('./course-assignments/CourseAssignmentFormPages').then((module) => module.CreateCourseAssignmentPage),
    },
    {
      path: courseAssignmentRoute,
      titleId: 'entitlements.courseAssignments.details.title',
      page: () => import('./course-assignments/CourseAssignmentDetailsPage').then((module) => module.CourseAssignmentDetailsPage),
    },
    {
      path: editCourseAssignmentRoute,
      titleId: 'entitlements.courseAssignments.edit.title',
      page: () => import('./course-assignments/CourseAssignmentFormPages').then((module) => module.EditCourseAssignmentPage),
    },
    // The Student Dashboard and Current Training: the person's own, and a person's for an administrator (ENT-20).
    {
      path: studentDashboardPath,
      titleId: 'entitlements.myTraining.dashboard.title',
      page: () => import('./my-training/StudentDashboardPage').then((module) => module.StudentDashboardPage),
    },
    {
      path: myTrainingPath,
      titleId: 'entitlements.myTraining.current.title',
      page: () => import('./my-training/CurrentTrainingPage').then((module) => module.CurrentTrainingPage),
    },
    {
      path: personTrainingRoute,
      titleId: 'entitlements.myTraining.current.title',
      page: () => import('./my-training/CurrentTrainingPage').then((module) => module.CurrentTrainingPage),
    },
    // Redeem Training Key, which a redeem link opens (ENT-16).
    { path: redeemPath, titleId: 'entitlements.redeem.title', page: () => import('./redeem/RedeemTrainingKeyPage').then((module) => module.RedeemTrainingKeyPage) },
    // The Subscription Catalog: what the person's on-demand plans offer (ENT-21).
    {
      path: subscriptionCatalogPath,
      titleId: 'entitlements.subscriptionCatalog.eyebrow',
      page: () => import('./subscription-catalog/SubscriptionCatalogPage').then((module) => module.SubscriptionCatalogPage),
    },
    // Create Enrollment, Enrollment Details, Edit Enrollment and Create Retake (ENT-28).
    {
      path: createEnrollmentPath,
      titleId: 'entitlements.enrollments.create.title',
      page: () => import('./enrollments/EnrollmentFormPages').then((module) => module.CreateEnrollmentPage),
    },
    {
      path: enrollmentRoute,
      titleId: 'entitlements.enrollments.details.title',
      page: () => import('./enrollments/EnrollmentDetailsPage').then((module) => module.EnrollmentDetailsPage),
    },
    {
      path: editEnrollmentRoute,
      titleId: 'entitlements.enrollments.edit.title',
      page: () => import('./enrollments/EnrollmentFormPages').then((module) => module.EditEnrollmentPage),
    },
    {
      path: retakeEnrollmentRoute,
      titleId: 'entitlements.enrollments.retake.title',
      page: () => import('./enrollments/EnrollmentFormPages').then((module) => module.RetakeEnrollmentPage),
    },
    // Find Enrollments (ENT-07).
    {
      path: findEnrollmentsPath,
      titleId: 'entitlements.findEnrollments.find.title',
      page: () => import('./find-enrollments/FindEnrollmentsPage').then((module) => module.FindEnrollmentsPage),
    },
    // Find, Create, Details and Edit Class Vouchers; Manage Class Vouchers; Vouchers from Class Details (ENT-17).
    { path: vouchersPath, titleId: 'entitlements.vouchers.find.title', page: () => import('./vouchers/FindVouchersPage').then((module) => module.FindVouchersPage) },
    {
      path: createVouchersPath,
      titleId: 'entitlements.vouchers.create.title',
      page: () => import('./vouchers/VoucherFormPages').then((module) => module.CreateVouchersPage),
    },
    {
      path: voucherRoute,
      titleId: 'entitlements.vouchers.details.title',
      page: () => import('./vouchers/VoucherDetailsPage').then((module) => module.VoucherDetailsPage),
    },
    {
      path: editVoucherRoute,
      titleId: 'entitlements.vouchers.edit.title',
      page: () => import('./vouchers/VoucherFormPages').then((module) => module.EditVoucherPage),
    },
    {
      path: manageVouchersPath,
      titleId: 'entitlements.manageVouchers.title',
      page: () => import('./vouchers/ManageVouchersPage').then((module) => module.ManageVouchersPage),
    },
    {
      path: organizationVouchersRoute,
      titleId: 'entitlements.manageVouchers.title',
      page: () => import('./vouchers/ManageVouchersPage').then((module) => module.ManageVouchersPage),
    },
    {
      path: classVouchersRoute,
      titleId: 'entitlements.classVouchers.title',
      page: () => import('./vouchers/ClassVouchersPage').then((module) => module.ClassVouchersPage),
    },
    // Find Survey Grants and Grant Survey: surveys given outside a course (ENT-22).
    {
      path: surveyGrantsPath,
      titleId: 'entitlements.surveyGrants.find.title',
      page: () => import('./survey-grants/FindSurveyGrantsPage').then((module) => module.FindSurveyGrantsPage),
    },
    {
      path: grantSurveyPath,
      titleId: 'entitlements.surveyGrants.create.title',
      page: () => import('./survey-grants/GrantSurveyPage').then((module) => module.GrantSurveyPage),
    },
    // Find, Create, Details and Edit Custom Field: the custom fields on enrollments and plans (ENT-23).
    {
      path: customFieldsPath,
      titleId: 'entitlements.customFields.find.title',
      page: () => import('./custom-fields/FindCustomFieldsPage').then((module) => module.FindCustomFieldsPage),
    },
    {
      path: createCustomFieldPath,
      titleId: 'entitlements.customFields.create.title',
      page: () => import('./custom-fields/CustomFieldFormPages').then((module) => module.CreateCustomFieldPage),
    },
    {
      path: customFieldRoute,
      titleId: 'entitlements.customFields.details.title',
      page: () => import('./custom-fields/CustomFieldDetailsPage').then((module) => module.CustomFieldDetailsPage),
    },
    {
      path: editCustomFieldRoute,
      titleId: 'entitlements.customFields.edit.title',
      page: () => import('./custom-fields/CustomFieldFormPages').then((module) => module.EditCustomFieldPage),
    },
    // Enrollments from Class Details, and Bulk Enrollment (ENT-05).
    {
      path: classEnrollmentsRoute,
      titleId: 'entitlements.classEnrollments.list.title',
      page: () => import('./class-enrollments/ClassEnrollmentsPage').then((module) => module.ClassEnrollmentsPage),
    },
    {
      path: bulkEnrollmentPath,
      titleId: 'entitlements.classEnrollments.bulk.title',
      page: () => import('./class-enrollments/BulkEnrollmentPage').then((module) => module.BulkEnrollmentPage),
    },
  ],
  // Every signed-in person's own (ENT-20).
  nav: [{ id: 'student-dashboard', labelId: 'entitlements.myTraining.dashboard.title', path: studentDashboardPath }],
  adminHome: {
    links: [
      {
        id: 'find-enrollments',
        category: 'class-enrollments',
        labelId: 'entitlements.findEnrollments.find.title',
        path: findEnrollmentsPath,
        permission: readEnrollmentsPermission,
      },
      {
        id: 'create-enrollment',
        category: 'class-enrollments',
        labelId: 'entitlements.enrollments.create.title',
        path: createEnrollmentPath,
        permission: writeEnrollmentsPermission,
      },
      {
        id: 'bulk-enrollment',
        category: 'class-enrollments',
        labelId: 'entitlements.classEnrollments.bulk.title',
        path: bulkEnrollmentPath,
        permission: writeEnrollmentsPermission,
      },
      {
        id: 'find-training-keys',
        category: 'training-keys',
        labelId: 'entitlements.trainingKeys.find.title',
        path: trainingKeysPath,
        permission: readTrainingKeysPermission,
      },
      {
        id: 'create-training-keys',
        category: 'training-keys',
        labelId: 'entitlements.trainingKeys.create.title',
        path: createTrainingKeysPath,
        permission: writeTrainingKeysPermission,
      },
      { id: 'find-class-vouchers', category: 'class-vouchers', labelId: 'entitlements.vouchers.find.title', path: vouchersPath, permission: readVouchersPermission },
      {
        id: 'create-class-vouchers',
        category: 'class-vouchers',
        labelId: 'entitlements.vouchers.create.title',
        path: createVouchersPath,
        permission: writeVouchersPermission,
      },
      {
        id: 'manage-class-vouchers',
        category: 'class-vouchers',
        labelId: 'entitlements.manageVouchers.title',
        path: manageVouchersPath,
        permission: readVouchersPermission,
      },
      { id: 'find-plans', category: 'subscriptions', labelId: 'entitlements.plans.find.title', path: plansPath, permission: readPlansPermission },
      { id: 'create-plan', category: 'subscriptions', labelId: 'entitlements.plans.create.title', path: createPlanPath, permission: writePlansPermission },
      { id: 'find-plan-tags', category: 'subscriptions', labelId: 'entitlements.planTags.find.title', path: planTagsPath, permission: readPlanTagsPermission },
      {
        id: 'create-plan-tag',
        category: 'subscriptions',
        labelId: 'entitlements.planTags.create.title',
        path: createPlanTagPath,
        permission: writePlanTagsPermission,
      },
      {
        id: 'find-subscriptions',
        category: 'subscriptions',
        labelId: 'entitlements.subscriptions.find.title',
        path: subscriptionsPath,
        permission: readSubscriptionsPermission,
      },
      {
        id: 'create-subscription',
        category: 'subscriptions',
        labelId: 'entitlements.subscriptions.create.title',
        path: createSubscriptionPath,
        permission: writeSubscriptionsPermission,
      },
      {
        id: 'create-path-subscription',
        category: 'subscriptions',
        labelId: 'entitlements.subscriptions.createPath.title',
        path: createPathSubscriptionPath,
        permission: writeSubscriptionsPermission,
      },
      {
        id: 'find-course-assignments',
        category: 'course-assignments',
        labelId: 'entitlements.courseAssignments.find.title',
        path: courseAssignmentsPath,
        permission: readSubscriptionsPermission,
      },
      {
        id: 'create-course-assignment',
        category: 'course-assignments',
        labelId: 'entitlements.courseAssignments.create.title',
        path: createCourseAssignmentPath,
        permission: writeSubscriptionsPermission,
      },
      { id: 'find-survey-grants', category: 'surveys', labelId: 'entitlements.surveyGrants.find.title', path: surveyGrantsPath, permission: readSurveyGrantsPermission },
      { id: 'grant-survey', category: 'surveys', labelId: 'entitlements.surveyGrants.create.title', path: grantSurveyPath, permission: writeSurveyGrantsPermission },
      // Enrollment and plan custom fields, beside the enrollments and the plans they are on (ENT-23).
      {
        id: 'find-enrollment-custom-fields',
        category: 'class-enrollments',
        labelId: 'entitlements.customFields.find.title',
        path: customFieldsPath,
        permission: readCustomFieldsPermission,
      },
      {
        id: 'find-plan-custom-fields',
        category: 'subscriptions',
        labelId: 'entitlements.customFields.find.title',
        path: customFieldsPath,
        permission: readCustomFieldsPermission,
      },
      {
        id: 'create-custom-field',
        category: 'class-enrollments',
        labelId: 'entitlements.customFields.create.title',
        path: createCustomFieldPath,
        permission: writeCustomFieldsPermission,
      },
    ],
  },
  slots: [
    // Entitlements' part of "Download my data" on My Profile (ENT-32).
    { kind: 'link', slot: myDataSlot, id: 'entitlements.my-data', labelId: 'entitlements.myData.part', path: myDataApi },
    {
      // The person's course assignments, from User Details' side menu (ENT-11).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'course-assignments',
      labelId: 'entitlements.courseAssignments.user.find',
      icon: ClipboardList,
      path: (context) => personCourseAssignmentsPath(context.recordId ?? ''),
    },
    {
      // Create Course Assignment with the person chosen, from User Details' side menu (ENT-11).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'create-course-assignment',
      isCreate: true,
      labelId: 'entitlements.courseAssignments.user.create',
      permission: writeSubscriptionsPermission,
      icon: FilePlus2,
      path: (context) => createPersonCourseAssignmentPath(context.recordId ?? ''),
    },
    {
      // The person's subscriptions, from User Details' side menu (ENT-10).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'subscriptions',
      labelId: 'entitlements.subscriptions.user.find',
      icon: CalendarClock,
      path: (context) => personSubscriptionsPath(context.recordId ?? ''),
    },
    {
      // Create Subscription with the person chosen, from User Details' side menu (ENT-10).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'create-subscription',
      isCreate: true,
      labelId: 'entitlements.subscriptions.user.create',
      permission: writeSubscriptionsPermission,
      icon: Plus,
      path: (context) => createPersonSubscriptionPath(context.recordId ?? ''),
    },
    {
      // The person's class enrollments, from User Details' side menu (ENT-07).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'class-enrollments',
      labelId: 'entitlements.findEnrollments.user.find',
      permission: readEnrollmentsPermission,
      isPersonRecord: true,
      icon: PlayCircle,
      path: (context) => personEnrollmentsPath(context.recordId ?? ''),
    },
    {
      // Create Class Enrollment with the person chosen, from User Details' side menu (ENT-28).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'create-class-enrollment',
      isCreate: true,
      labelId: 'entitlements.enrollments.user.create',
      permission: writeEnrollmentsPermission,
      icon: UserPlus,
      path: (context) => createPersonEnrollmentPath(context.recordId ?? ''),
    },
    {
      // The `entitlements` settings group on Create and Edit Organization (ENT-26).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'entitlements',
      labelId: 'entitlements.settings.label',
      component: () => import('./settings/EntitlementsSettingsSection').then((module) => module.EntitlementsSettingsSection),
    },
    {
      // The Self-Paced Payment Types tab and the Advanced fields on Create and Edit Course (ENT-02).
      kind: 'tab',
      slot: courseFormSectionsSlot,
      id: 'payment-types',
      labelId: 'entitlements.courseAccess.payment.label',
      component: () => import('./course-access/CourseAccessParts').then((module) => module.PaymentTypesTab),
    },
    {
      // The Subscription Profiles tab on Create and Edit Course: the plans the course is in (ENT-08).
      kind: 'tab',
      slot: courseFormSectionsSlot,
      id: 'subscription-plans',
      labelId: 'entitlements.plans.course.label',
      component: () => import('./plans/CoursePlansTab').then((module) => module.CoursePlansTab),
    },
    {
      kind: 'panel',
      slot: courseAdvancedFieldsSlot,
      id: 'course-access',
      component: () => import('./course-access/CourseAccessParts').then((module) => module.AdvancedAccessFields),
    },
    {
      // Enrollments and Create Enrollment in Class Details' sidebar (ENT-05).
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'enrollments',
      labelId: 'entitlements.classEnrollments.list.title',
      permission: readEnrollmentsPermission,
      icon: PlayCircle,
      path: (context) => classEnrollmentsPath(context.recordId ?? ''),
    },
    {
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'create-enrollment',
      isCreate: true,
      labelId: 'entitlements.enrollments.create.title',
      permission: writeEnrollmentsPermission,
      icon: UserPlus,
      path: (context) => createClassEnrollmentPath(context.recordId ?? ''),
    },
    // The status and room controls on Classes' roster, and Add Students and Save Changes beside its title (ENT-05).
    column<RosterCellValue>({
      kind: 'column',
      slot: classSlots.rosterStatus,
      id: 'enrollment-status',
      headerId: 'entitlements.classEnrollments.roster.column.status',
      // Each row gets its class and page of ids, which its cell reads once for the page.
      load: (ids, context) => Promise.resolve(new Map(ids.map((id) => [id, { classId: context.recordId ?? '', page: ids }]))),
      cell: () => import('./class-enrollments/RosterControls').then((module) => module.RosterStatusCell),
    }),
    column<RosterCellValue>({
      kind: 'column',
      slot: classSlots.rosterRoom,
      id: 'enrollment-room',
      headerId: 'entitlements.classEnrollments.roster.column.room',
      load: (ids, context) => Promise.resolve(new Map(ids.map((id) => [id, { classId: context.recordId ?? '', page: ids }]))),
      cell: () => import('./class-enrollments/RosterControls').then((module) => module.RosterRoomCell),
    }),
    {
      kind: 'action',
      slot: classSlots.rosterActions,
      id: 'enrollment-actions',
      component: () => import('./class-enrollments/RosterControls').then((module) => module.RosterActions),
    },
    {
      // Training Keys in Class Details' sidebar: the class's single-use keys (ENT-15).
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'training-keys',
      labelId: 'entitlements.classKeys.sidebar',
      permission: readTrainingKeysPermission,
      icon: KeyRound,
      path: (context) => classTrainingKeysPath(context.recordId ?? ''),
    },
    {
      // Vouchers in Class Details' sidebar: the vouchers on the class (ENT-17).
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'vouchers',
      labelId: 'entitlements.classVouchers.sidebar',
      permission: readVouchersPermission,
      // Through the class's organization: an issuer's read-only voucher-issuer grant does not open the page.
      permissionScope: voucherPageScopes,
      icon: Ticket,
      path: (context) => classVouchersPath(context.recordId ?? ''),
    },
    {
      // Manage Class Vouchers in Organization Details' sidebar: the organization's own (ENT-17).
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'manage-class-vouchers',
      labelId: 'entitlements.manageVouchers.title',
      permission: readVouchersPermission,
      permissionScope: voucherPageScopes,
      icon: Ticket,
      path: (context) => organizationVouchersPath(context.recordId ?? ''),
    },
    {
      // The Event Training Key field on Create and Edit Class (ENT-15).
      kind: 'panel',
      slot: classSlots.formSections,
      id: 'event-key',
      component: () => import('./class-keys/EventKeyField').then((module) => module.EventKeyField),
    },
    {
      // The Event Training Key on Class Details (ENT-15).
      kind: 'panel',
      slot: detailsSlots(classDetailsSlot).panels,
      id: 'event-key',
      component: () => import('./class-keys/EventKeyPanel').then((module) => module.EventKeyPanel),
    },
    {
      // Class Training Key beside each of today's sessions on the instructor dashboard (ENT-15 follow-up, story 109741).
      kind: 'action',
      slot: classSlots.dashboardSessionActions,
      id: 'event-key',
      component: () => import('./class-keys/SessionEventKey').then((module) => module.SessionEventKey),
    },
    {
      // A person's Current Training, from User Details' side menu, for whoever may read either part of it (ENT-20).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'current-training',
      labelId: 'entitlements.myTraining.current.title',
      permission: [readEnrollmentsPermission, readSubscriptionsPermission],
      isPersonRecord: true,
      icon: Clock,
      path: (context) => personTrainingPath(context.recordId ?? ''),
    },
    {
      // The instructor dashboard's Current Training quick link: the instructor's own (ENT-20).
      kind: 'link',
      slot: classSlots.dashboardLinks,
      id: 'current-training',
      labelId: 'entitlements.myTraining.current.title',
      path: myTrainingPath,
    },
    {
      // Enroll Me, Request Enrollment and Cancel on the learner class page (ENT-04).
      kind: 'action',
      slot: detailsSlots(classSlots.learnerPage).actions,
      id: 'enrollment-actions',
      component: () => import('./enrollment-actions/EnrollmentActions').then((module) => module.EnrollmentActions),
    },
    {
      // My Surveys in the Student Dashboard's side column: surveys given outside a course (ENT-22).
      kind: 'panel',
      slot: studentDashboardSlots.side,
      id: 'my-surveys',
      component: () => import('./survey-grants/MySurveysPanel').then((module) => module.MySurveysPanel),
    },
    {
      // Course assignments on the Student Dashboard's week at a glance and month calendar (ENT-31).
      kind: 'panel',
      slot: classSlots.studentCalendarItems,
      id: 'course-assignments',
      component: () => import('./my-training/CourseAssignmentCalendarItems').then((module) => module.CourseAssignmentCalendarItems),
    },
    {
      // Enroll, or Continue, beside the course's card on its learner page: self-assignment (ENT-13).
      kind: 'action',
      slot: courseCatalogSlots.actions,
      id: 'self-assignment',
      component: () => import('./self-assignment/SelfAssignAction').then((module) => module.SelfAssignAction),
    },
  ],
}
